import React from 'react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { BlurText } from '../animations/BlurText';
// Section eyebrow label with number
export function SectionLabel({
  number,
  label,
  className = ''
}: {
  number?: string;
  label: string;
  className?: string;
}) {
  return <div className={`flex items-center gap-3 ${className}`} data-id="element-1341">
      {number && <span className="font-mono text-xs text-[#1a8fd4] tracking-widest" data-id="element-1342">
          {number}
        </span>}
      <span className="h-px w-8 bg-[#1a8fd4]/40" data-id="element-1343" />
      <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60" data-id="element-1344">
        {label}
      </span>
    </div>;
}
// Section heading with blur-text animation on scroll into view
export function SectionHeading({
  children,
  className = '',
  size = 'lg',
  animate = true
}: {
  children: React.ReactNode;
  className?: string;
  size?: 'lg' | 'xl' | '2xl';
  animate?: boolean;
}) {
  const sizes = {
    lg: 'text-3xl md:text-4xl lg:text-5xl',
    xl: 'text-4xl md:text-5xl lg:text-6xl',
    '2xl': 'text-5xl md:text-7xl lg:text-8xl'
  };
  // If children is a simple string, wrap it in BlurText
  if (animate && typeof children === 'string') {
    return <h2 className={`${sizes[size]} font-semibold text-white tracking-tight leading-[1.05] ${className}`} data-id="element-1345">
        <BlurText text={children} splitBy="word" data-id="element-1346" />
      </h2>;
  }
  return <h2 className={`${sizes[size]} font-semibold text-white tracking-tight leading-[1.05] ${className}`} data-id="element-1347">
      {children}
    </h2>;
}
// Primary CTA button
export function PrimaryButton({
  href,
  children,
  className = '',
  size = 'md'
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-3 text-sm',
    lg: 'px-7 py-4 text-base'
  };
  return <a href={href} className={`relative overflow-hidden inline-flex items-center justify-center gap-2 ${sizes[size]} bg-white text-black font-medium rounded-full transition-all hover:bg-white/90 group ${className}`} data-id="element-1348">
      <span className="relative z-10" data-id="element-1349">{children}</span>
      <ArrowRight className="w-4 h-4 relative z-10 transition-transform group-hover:translate-x-0.5" data-id="element-1350" />
      <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-1000 bg-gradient-to-r from-transparent via-black/10 to-transparent" data-id="element-1351" />
    </a>;
}
// Secondary CTA button
export function SecondaryButton({
  href,
  children,
  className = '',
  size = 'md'
}: {
  href: string;
  children: React.ReactNode;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const sizes = {
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-3 text-sm',
    lg: 'px-7 py-4 text-base'
  };
  return <a href={href} className={`inline-flex items-center justify-center gap-2 ${sizes[size]} bg-white/5 hover:bg-white/10 border border-white/10 hover:border-white/20 text-white font-medium rounded-full transition-all group ${className}`} data-id="element-1352">
      <span data-id="element-1353">{children}</span>
      <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" data-id="element-1354" />
    </a>;
}
// Section wrapper with optional grid background
export function Section({
  children,
  id,
  className = '',
  gridBg = false,
  py = 'lg'
}: {
  children: React.ReactNode;
  id?: string;
  className?: string;
  gridBg?: boolean;
  py?: 'sm' | 'md' | 'lg' | 'xl';
}) {
  const paddings = {
    sm: 'py-16',
    md: 'py-24',
    lg: 'py-32',
    xl: 'py-40'
  };
  return <section id={id} className={`relative w-full ${paddings[py]} ${className}`} data-id="element-1355">
      {gridBg && <div className="absolute inset-0 bg-grid-fine opacity-50 pointer-events-none" data-id="element-1356" />}
      <div className="relative max-w-7xl mx-auto px-6 lg:px-8" data-id="element-1357">{children}</div>
    </section>;
}